import { EventEmitter } from 'node:events';
import WebSocket from 'ws';
import { Methods, UnboundedMethods } from '../api/methods.js';
import { EncryptedSession } from '../crypto/session.js';
import type { StoredCredentials } from '../crypto/storage.js';
import type { JsonRpcResponse, VersionResponse } from '../types.js';
import type { DeviceConfig } from './config.js';
import { deviceEndpoint } from './endpoint.js';
import { ClientError, connectionError, RpcError, timeoutError } from './errors.js';
import type { TraceWriter } from './trace.js';

const ENCRYPTION_REQUIRED_CODE = -32002;
const MAX_CONNECT_RATE_LIMIT_RETRIES = 4;
const INITIAL_CONNECT_RETRY_DELAY_MS = 750;
const MAX_CONNECT_RETRY_DELAY_MS = 3_000;

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
  timer?: ReturnType<typeof setTimeout>;
  method: string;
  started: number;
}

export interface ZaparooClientOptions {
  credentials?: StoredCredentials;
  connectTimeoutMs?: number;
  requestTimeoutMs?: number;
  trace?: TraceWriter;
}

export interface ZaparooClientEvents {
  notification: [method: string, params: unknown, deviceId: string];
}

export class ZaparooClient extends EventEmitter<ZaparooClientEvents> {
  private ws: WebSocket | null = null;
  private requestIdCounter = 0;
  private readonly pending = new Map<string, PendingRequest>();
  private encryptedSession: EncryptedSession | null = null;
  private readonly requestTimeoutMs: number;
  private versionInfo?: VersionResponse;
  private closing = false;

  constructor(
    readonly device: DeviceConfig,
    private readonly options: ZaparooClientOptions = {},
  ) {
    super();
    this.requestTimeoutMs = options.requestTimeoutMs ?? 30_000;
  }

  get info(): VersionResponse | undefined {
    return this.versionInfo;
  }

  get encrypted(): boolean {
    return this.encryptedSession !== null;
  }

  async connect(): Promise<VersionResponse> {
    if (this.ws?.readyState === WebSocket.OPEN && this.versionInfo) return this.versionInfo;
    if (this.ws) await this.close();

    this.closing = false;
    const endpoint = deviceEndpoint(this.device);
    const connectTimeoutMs = this.options.connectTimeoutMs ?? 30_000;
    const deadline = Date.now() + connectTimeoutMs;
    let retries = 0;
    while (true) {
      const remainingMs = deadline - Date.now();
      if (remainingMs <= 0) {
        throw timeoutError(`WebSocket connect timed out after ${connectTimeoutMs}ms`);
      }
      let attemptSocket: WebSocket | null = null;
      try {
        const opening = this.openWebSocket(endpoint.url, endpoint.headers, remainingMs);
        attemptSocket = this.ws;
        await opening;
        break;
      } catch (error) {
        const failedSocket = attemptSocket ?? this.ws;
        this.ws = null;
        failedSocket?.removeAllListeners();
        failedSocket?.on('error', () => {});
        if (failedSocket && failedSocket.readyState !== WebSocket.CLOSED) failedSocket.terminate();
        if (
          !(error instanceof ClientError) ||
          error.kind !== 'rate-limit' ||
          retries >= MAX_CONNECT_RATE_LIMIT_RETRIES
        ) {
          throw error;
        }
        const delayMs = Math.min(
          INITIAL_CONNECT_RETRY_DELAY_MS * 2 ** retries,
          MAX_CONNECT_RETRY_DELAY_MS,
        );
        if (Date.now() + delayMs >= deadline) {
          throw timeoutError(`WebSocket connect timed out after ${connectTimeoutMs}ms`);
        }
        retries++;
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    if (this.options.credentials) {
      this.encryptedSession = EncryptedSession.create(
        this.options.credentials.authToken,
        Buffer.from(this.options.credentials.pairingKey, 'hex'),
      );
    } else {
      this.encryptedSession = null;
    }

    const version = await this.requestInternal<VersionResponse>(Methods.Version);
    this.versionInfo = version;
    return version;
  }

  private openWebSocket(
    url: string,
    headers: Record<string, string> | undefined,
    timeoutMs: number,
  ): Promise<void> {
    const ws = new WebSocket(url, { headers });
    this.ws = ws;
    ws.on('message', (data) => this.onMessage(data));
    ws.on('close', (code, reason) => this.onClose(code, reason.toString()));
    ws.on('error', (error) => this.onSocketError(error));

    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (error?: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        ws.off('open', onOpen);
        ws.off('unexpected-response', onUnexpectedResponse);
        if (error) reject(error);
        else resolve();
      };
      const onOpen = () => finish();
      const onUnexpectedResponse = (_request: unknown, response: { statusCode: number }) => {
        const kind =
          response.statusCode === 401
            ? 'api-auth'
            : response.statusCode === 429
              ? 'rate-limit'
              : 'connection';
        finish(
          new ClientError(kind, `WebSocket upgrade rejected with HTTP ${response.statusCode}`, {
            statusCode: response.statusCode,
          }),
        );
        ws.terminate();
      };
      const timer = setTimeout(() => {
        finish(timeoutError(`WebSocket connect timed out after ${timeoutMs}ms`));
        ws.terminate();
      }, timeoutMs);
      ws.once('open', onOpen);
      ws.once('unexpected-response', onUnexpectedResponse);
      ws.once('close', (code, reason) => {
        finish(connectionError(`WebSocket closed during connect (${code}): ${reason.toString()}`));
      });
      ws.once('error', (error) => finish(connectionError(error.message)));
    });
  }

  async request<T = unknown>(method: string, params?: unknown): Promise<T> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) await this.connect();
    return this.requestInternal<T>(method, params);
  }

  async close(): Promise<void> {
    this.closing = true;
    this.rejectPending(connectionError('Connection closed by client'));
    const ws = this.ws;
    this.ws = null;
    this.versionInfo = undefined;
    this.encryptedSession = null;
    if (!ws || ws.readyState === WebSocket.CLOSED) return;
    await new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        ws.terminate();
        resolve();
      }, 250);
      ws.once('close', () => {
        clearTimeout(timer);
        resolve();
      });
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close();
      else {
        clearTimeout(timer);
        resolve();
      }
    });
  }

  private requestInternal<T = unknown>(method: string, params?: unknown): Promise<T> {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(connectionError('WebSocket not open'));
    }
    const id = `${++this.requestIdCounter}`;
    const request: Record<string, unknown> = { jsonrpc: '2.0', id, method };
    if (params !== undefined) request.params = params;
    const payload = JSON.stringify(request);
    this.options.trace?.write({
      timestamp: new Date().toISOString(),
      deviceId: this.device.id,
      direction: 'request',
      method,
      id,
      data: params ?? null,
    });

    return new Promise<T>((resolve, reject) => {
      const unbounded = UnboundedMethods.has(method as never);
      const timer =
        unbounded || this.requestTimeoutMs <= 0
          ? undefined
          : setTimeout(() => {
              this.pending.delete(id);
              reject(timeoutError(`Request ${method} timed out after ${this.requestTimeoutMs}ms`));
            }, this.requestTimeoutMs);
      this.pending.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer,
        method,
        started: Date.now(),
      });
      ws.send(this.encryptOutgoing(payload), (error) => {
        if (!error) return;
        if (timer) clearTimeout(timer);
        this.pending.delete(id);
        reject(connectionError(error.message));
      });
    });
  }

  private onMessage(data: WebSocket.RawData): void {
    let msg: JsonRpcResponse & { method?: string; params?: unknown };
    try {
      msg = this.decryptIncoming(data.toString()) as JsonRpcResponse & {
        method?: string;
        params?: unknown;
      };
    } catch (error) {
      const protocolError = new ClientError(
        'protocol',
        error instanceof Error ? error.message : String(error),
      );
      this.rejectPending(protocolError);
      this.ws?.close();
      return;
    }

    if (msg.id !== undefined) {
      const id = String(msg.id);
      const pending = this.pending.get(id);
      if (!pending) return;
      this.pending.delete(id);
      if (pending.timer) clearTimeout(pending.timer);
      this.options.trace?.write({
        timestamp: new Date().toISOString(),
        deviceId: this.device.id,
        direction: 'response',
        method: pending.method,
        id,
        data: msg.error ? { error: msg.error } : msg.result,
        durationMs: Date.now() - pending.started,
      });
      if (msg.error?.code === ENCRYPTION_REQUIRED_CODE) {
        pending.reject(
          new ClientError(
            'encryption-required',
            `Encryption required for ${this.device.id}; start pairing on Core, then run zaparoo-cli pair complete --pin <pin>`,
            msg.error,
          ),
        );
      } else if (msg.error) {
        pending.reject(new RpcError(msg.error));
      } else {
        pending.resolve(msg.result);
      }
      return;
    }

    if (!msg.method) return;
    this.options.trace?.write({
      timestamp: new Date().toISOString(),
      deviceId: this.device.id,
      direction: 'notification',
      method: msg.method,
      data: msg.params ?? null,
    });
    this.emit('notification', msg.method, msg.params, this.device.id);
  }

  private onClose(code: number, reason: string): void {
    if (this.ws?.readyState === WebSocket.CLOSED) this.ws = null;
    if (this.closing) return;
    const error =
      this.encryptedSession && !this.versionInfo
        ? new ClientError(
            'pairing-rejected',
            `Encrypted session rejected by ${this.device.id}; saved pairing credentials may be stale or belong to a different endpoint`,
            { code, reason },
          )
        : connectionError(`WebSocket closed (${code})${reason ? `: ${reason}` : ''}`);
    this.rejectPending(error);
  }

  private onSocketError(error: Error): void {
    if (this.closing) return;
    this.rejectPending(connectionError(error.message));
  }

  private rejectPending(error: Error): void {
    for (const [id, pending] of this.pending) {
      if (pending.timer) clearTimeout(pending.timer);
      pending.reject(error);
      this.pending.delete(id);
    }
  }

  private encryptOutgoing(payload: string): string {
    return this.encryptedSession ? this.encryptedSession.encryptAndFrame(payload) : payload;
  }

  private decryptIncoming(raw: string): JsonRpcResponse {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (this.encryptedSession) {
      if (typeof parsed.e !== 'string') {
        throw new Error('Received plaintext frame during an encrypted session');
      }
      return JSON.parse(this.encryptedSession.decrypt(parsed.e)) as JsonRpcResponse;
    }
    if ('e' in parsed) {
      throw new Error('Received encrypted frame without an active encrypted session');
    }
    return parsed as unknown as JsonRpcResponse;
  }
}
